import * as React from 'react';
import {
    Route,
    useLocation,
    Routes,
    useNavigate,
    Navigate,
} from 'react-router-dom';
import {
    Mutation,
    MutationCache,
    Query,
    QueryCache,
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import numbro from 'numbro';
// @ts-expect-error [TS migration] Was not detected by flow
import languages from 'numbro/dist/languages.min';

import Loading from 'components/Loading';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import SnackbarNotification from 'components/SnackbarNotification/SnackbarNotification';
import LoginPage from 'routes/LoginPage';
import ResetPassword from 'routes/ResetPassword';

import Explore from 'routes/Explore';
import Dashboard from 'routes/Dashboard';
import IntegrationsList from 'routes/IntegrationsList';
import Profile from 'routes/Profile';
import Mappings from 'routes/Mappings';
import BudgetSettings from 'routes/Admin/Budget/BudgetSettings';
import UserManagement from 'routes/Admin/UserManagement/UserManagement';
import Home from 'routes/Home/components/Home';
import BudgetProposal from 'routes/BudgetModule/components/BudgetProposal/BudgetProposal';
import Resource from 'routes/BudgetModule/components/BudgetProposal/Resource/Resource';
import BudgetWithProvider from 'routes/BudgetModule/components/BudgetWithProvider/BudgetWithProvider';
import BudgetLibrary from 'routes/BudgetModule/components/shared/BudgetLibrary/BudgetLibrary';
import Operation360 from 'routes/BudgetModule/components/Operation360/Operation360';
import BudgetOverview from 'routes/BudgetModule/components/BudgetOverview/BudgetOverview';
import Indicators from 'routes/Indicators/Indicators';
import './_app.scss';
import {
    ADMIN,
    BACK_OFFICE,
    BUDGET,
    BUDGET_USER,
    DECISION,
    DECISION_USER,
    HUMAN_RESOURCES,
    HUMAN_RESOURCES_USER,
} from 'business/constants';
import MaintenanceDialog from 'routes/BudgetModule/components/shared/MaintenanceDialog';
import AppWithSideBar from 'components/SideBar/AppWithSideBar';
import DecisionWithAccessCheck from 'components/ModuleAccess/DecisionWithAccessCheck';
import IndicatorsWithAccessCheck from 'components/ModuleAccess/IndicatorsWithAccessCheck';
import BudgetWithAccessCheck from 'components/ModuleAccess/BudgetWithAccessCheck';
import NotFound from 'components/Errors/NotFound';
import PublicDashboardNotAccessiblePageWithRedirection from 'components/PublicDashboardNotAccessiblePage';
import HumanResourcesWithAccessCheck from 'components/ModuleAccess/HumanResourcesWithAccessCheck';
import HumanResourcesProvider from 'routes/HumanResourcesModule/components/contexts/HumanResourcesProvider';
import PayStateSynthesis from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/PayStateSynthesis';
import SupportRoutes from 'routes/Support/SupportRoutes';
import ExploreWithAccessCheck from 'components/ModuleAccess/ExploreWithAccessCheck';
import CellarRoutes from 'routes/Cellar/CellarRoutes';
import MaintenancePage from 'routes/Maintenance';
import SimulationSynthesis from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/SimulationSynthesis';
import { getPublicToken } from 'components/QueryPerimeter/hooks/useGetTreeData';
import { isCurrentlyOnPublicDashboard } from 'helpers/dashboardHelpers';
import {
    trackAnonymousUser,
    trackUser,
    updatePendoModules,
} from 'helpers/trackerHelpers';
import PayStateHome from 'routes/HumanResourcesModule/PayState/PayStateHome/PayStateHome';
import SimulationHome from 'routes/HumanResourcesModule/Simulation/SimulationHome/SimulationHome';
import HrSettings from 'routes/HumanResourcesModule/Settings/HRSettings';
import ObsoleteOrgChartSimulationProvider from 'routes/HumanResourcesModule/Simulation/contexts/ObsoleteOrgChartSimulationProvider';
import { useEffect } from 'react';
import { useSelector } from 'store/hooks';
import { dispatch } from 'store/store';
import ResourceWithRouting from 'routes/BudgetModule/components/BudgetProposal/Resource/ResourceWithRouting';
import routes from './routes';
import RetrospectiveHome from 'routes/HumanResourcesModule/Retrospective/RetrospectiveHome/RetrospectiveHome';
import RetrospectiveSynthesis from 'routes/HumanResourcesModule/Retrospective/RetrospectiveSynthesis/RetrospectiveSynthesis';
import Initialisation from 'routes/Admin/Budget/Initialisation/Initialisation';
import { useIsEssential } from 'hooks/client';
import { useDeepCompareEffect } from 'helpers/reactUseHelper';
import Forbidden from 'components/Errors/Forbidden';
import BudgetAdminSettingsWithAccessCheck from 'components/ModuleAccess/BudgetAdminSettingsWithAccessCheck';
import BudgetOverviewWithAccessCheck from 'components/ModuleAccess/BudgetOverviewWithAccessCheck';
import CallToActionDecision from 'components/ModuleAccess/CallToActions/CallToActionDecision';
import CallToActionBudget from 'components/ModuleAccess/CallToActions/CallToActionBudget';
import CallToActionHumanResources from 'components/ModuleAccess/CallToActions/CallToActionHumanResources';
import {
    useAddHubspot,
    useAddPendo,
} from 'components/App/hooks/thirdPartyScripts';
import { HttpError } from 'store/APIClient';
import Dashboards from 'routes/AllDashboards/Dashboards';

numbro.registerLanguage(languages['fr-FR']);
numbro.setLanguage('fr-FR');

// Change the default error type of react query to HttpError
// which contain status and message.
declare module '@tanstack/react-query' {
    interface Register {
        defaultError: HttpError;
    }
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: (failureCount: number, error: { status?: number }) => {
                if (!error.status) {
                    return false;
                }
                // we do not want to retry on 4XX and 500 errors, as they are unlikely to yield different results
                // we might want to retry on 522 (connection timeout) though, which can happen if the server is rebooting
                return error.status > 500 && failureCount < 3;
            },
        },
    },
    queryCache: new QueryCache({
        /* This onError callback is called for every query. To enable the snackbar notification,
         * the query needs to have { meta: { useGlobalOnError: true } } in its options. */
        onError: (error: HttpError, query: Query) => {
            if (!query.meta?.useGlobalOnError) return;
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
    }),
    mutationCache: new MutationCache({
        /* These onError callback are called for every mutation. To enable the snackbar notification,
         * the mutation needs to have { meta: { useGlobalOnError: true } } in its options.
         * If you declare a custom onError on the mutation, it will override this global one, so don't forget to handle
         * the notification there too. */
        onError: (
            error: HttpError,
            _variables: unknown,
            _context: unknown,
            mutation: Mutation,
        ) => {
            if (!mutation.meta?.useGlobalOnError) return;
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
    }),
});

export default function App(): React.ReactElement {
    useAddHubspot();
    useAddPendo();
    const location = useLocation();
    const navigate = useNavigate();
    const isEssential = useIsEssential();

    const {
        isLoading,
        isAuthenticated,
        initialLoad,
        user,
        client,
        homePageUrl,
        hasAccessBudgetsGestion,
        isSupportUser,
        showSnack,
        snackAction,
        snackText,
        snackType,
        modules,
        clientLoadedAfterAuth,
    } = useSelector((s) => ({
        isLoading:
            s.loading.effects.vision.checkAuthStatus ||
            s.loading.effects.vision.fetchClientDetails,
        isAuthenticated: s.vision.isAuthenticated,
        initialLoad: s.vision.initialLoad,
        user: s.vision.user,
        client: s.vision.client,
        homePageUrl: s.vision.homePageUrl,
        hasAccessBudgetsGestion: s.vision.hasAccessBudgetsGestion,
        isSupportUser: s.vision.isSupportUser,
        showSnack: s.vision.showSnack,
        snackAction: s.vision.snackAction,
        snackText: s.vision.snackText,
        snackType: s.vision.snackType,
        modules: s.vision.client.modules,
        clientLoadedAfterAuth: s.vision.clientLoadedAfterAuth,
    }));
    const {
        closeSnack,
        checkAuthStatus,
        fetchClientDetails,
        fetchProtections,
        fetchScopes,
        logout,
    } = dispatch.vision;

    useEffect(() => {
        if (!localStorage.getItem('primaryColor')) {
            fetchClientDetails();
        }

        let shouldBlacklist = false;
        if (
            location.search &&
            location.search.includes('?token=') &&
            !localStorage.getItem('token')
        ) {
            // Used to log a new user when she arrives on a new url
            const token = location.search.split('token=')[1];
            shouldBlacklist = true;
            localStorage.setItem('token', token);
        }

        if (localStorage.getItem('token')) {
            // @ts-expect-error [react-router-upgrade] Uncovered during upgrade. TODO: Fix later.
            checkAuthStatus(shouldBlacklist);
        }
        // This behaviour is duplicated with didUpdate -> do we want this ?
        if (isAuthenticated) {
            fetchScopes();
            fetchProtections();
        }
        const isPublicDashboard = isCurrentlyOnPublicDashboard();
        if (isPublicDashboard) {
            trackAnonymousUser();
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchClientDetails();
            fetchScopes();
            fetchProtections();
            trackUser(user, isEssential);
        }
    }, [isAuthenticated]);

    useDeepCompareEffect(() => {
        updatePendoModules(modules);
    }, [modules]);

    const e2eClassName = isAuthenticated
        ? 'e2e-logged-in'
        : 'e2e-not-logged-in';

    // Default route
    const { pathname, search } = window.location;
    if (['/', '/vision'].includes(pathname) && !!homePageUrl) {
        navigate(homePageUrl);
        // NOTE - beware, the ^ is needed to not match dashboards with the word vision
        // in the slug
    } else if (pathname.match(/^\/vision\//)) {
        const newUrl = pathname.replace('/vision/', '/');
        navigate(`${newUrl}${search}`);
    }
    const isMaintenance = pathname.includes('/maintenance');
    const isPublicDashboard = isCurrentlyOnPublicDashboard();
    // We use this boolean to know wether we should show the login component
    // Without this it is always displayed, which is not what we want.
    // There is no easy way to avoid displaying the default component for every route with our setup
    const showLogin =
        !isAuthenticated &&
        !(isPublicDashboard || pathname.includes('/registration'));

    const publicToken = getPublicToken();

    if (!client.isValidBind) {
        return <NotFound />;
    }

    if (!isMaintenance && (isLoading || initialLoad)) {
        return (
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={client.theme}>
                    <Loading pastDelay />
                </ThemeProvider>
            </StyledEngineProvider>
        );
    }

    const hasDecision = client.modules?.includes(DECISION);
    const hasBudget = client.modules?.includes(BUDGET);
    const hasHumanResources = client.modules?.includes(HUMAN_RESOURCES);
    const hasBackOffice = client.modules?.includes(BACK_OFFICE);
    const isBudgetUser = user.roles.includes(BUDGET_USER);
    const isDecisionUser = user.roles.includes(DECISION_USER);
    const isHumanResourcesUser = user.roles.includes(HUMAN_RESOURCES_USER);
    const isAdminUser = user.roles.includes(ADMIN);
    const isAdminOrSupport = isAdminUser || isSupportUser;

    return (
        <QueryClientProvider client={queryClient}>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={client.theme}>
                    <div className={`vision-layout ${e2eClassName}`}>
                        <ReactQueryDevtools
                            initialIsOpen={false}
                            buttonPosition="bottom-right"
                        />
                        <AppWithSideBar
                            isAuthenticated={isAuthenticated}
                            isAdmin={Boolean(user && user.isAdmin)}
                            client={client}
                            logout={logout}
                            userRoles={user.roles}
                            // TODO StrictNullCheck : Try to remove "!"
                            homePageUrl={homePageUrl!}
                            isMaintenance={isMaintenance}
                            isPublicDashboard={isPublicDashboard}
                        >
                            {/* FIXME Switch considers only first layer child thus
                                doesn't work with Fragment. To be fixed when React
                                Router library version 6 is released. See
                                https://stackoverflow.com/questions/41474134/nested-routes-with-react-router-v4-v5/49321289#49321289 */}
                            <ErrorBoundary>
                                <Routes>
                                    <Route
                                        path={routes.activateAccount}
                                        element={
                                            <ResetPassword
                                                resetType="activate"
                                                homePageUrl={homePageUrl!}
                                            />
                                        }
                                    />
                                    <Route
                                        path={routes.resetPassword}
                                        element={
                                            <ResetPassword
                                                resetType="reset"
                                                homePageUrl={homePageUrl!}
                                            />
                                        }
                                    />
                                    {isMaintenance && (
                                        <Route
                                            path={routes.maintenancePage}
                                            element={<MaintenancePage />}
                                        />
                                    )}
                                    {isAuthenticated &&
                                        isSupportUser &&
                                        hasBackOffice &&
                                        SupportRoutes}
                                    {isAuthenticated &&
                                        client.modules &&
                                        clientLoadedAfterAuth && (
                                            <>
                                                <Route
                                                    path={routes.exploreChart}
                                                    key="exploreChartKey"
                                                    element={
                                                        <ExploreWithAccessCheck
                                                            isAdminUser={
                                                                isAdminUser
                                                            }
                                                            isSupportUser={
                                                                isSupportUser
                                                            }
                                                            hasDecision={
                                                                hasDecision
                                                            }
                                                        >
                                                            <Explore />
                                                        </ExploreWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.explore}
                                                    key="explore"
                                                    element={
                                                        <ExploreWithAccessCheck
                                                            isAdminUser={
                                                                isAdminUser
                                                            }
                                                            isSupportUser={
                                                                isSupportUser
                                                            }
                                                            hasDecision={
                                                                hasDecision
                                                            }
                                                        >
                                                            <Explore />
                                                        </ExploreWithAccessCheck>
                                                    }
                                                />
                                                {user.isAdmin && (
                                                    <Route
                                                        path={
                                                            routes.integrations
                                                        }
                                                        key="integrations"
                                                        element={
                                                            <IntegrationsList />
                                                        }
                                                    />
                                                )}
                                                {CellarRoutes}
                                                <Route
                                                    path={routes.profile}
                                                    key="profile"
                                                    element={<Profile />}
                                                />
                                                <Route
                                                    path={routes.adminTab}
                                                    key="adminTab"
                                                    element={
                                                        isAdminOrSupport ? (
                                                            <UserManagement />
                                                        ) : (
                                                            <Forbidden />
                                                        )
                                                    }
                                                />
                                                <Route
                                                    path={routes.adminBudget}
                                                    key="adminBudget"
                                                    element={
                                                        <BudgetAdminSettingsWithAccessCheck>
                                                            <BudgetWithProvider>
                                                                <BudgetSettings />
                                                            </BudgetWithProvider>
                                                        </BudgetAdminSettingsWithAccessCheck>
                                                    }
                                                >
                                                    <Route
                                                        path=":by"
                                                        key="adminBudgetBy"
                                                        element={
                                                            <BudgetSettings />
                                                        }
                                                    />
                                                </Route>
                                                {(hasAccessBudgetsGestion ||
                                                    isSupportUser) && (
                                                    <Route
                                                        path={
                                                            routes.initialisationBudget
                                                        }
                                                        key="initialisationBudget"
                                                        element={
                                                            <Initialisation />
                                                        }
                                                    />
                                                )}
                                                <Route
                                                    path={routes.mappings}
                                                    key="mappings"
                                                    element={<Mappings />}
                                                />
                                                <Route
                                                    path={routes.dashboard}
                                                    key="dashboardSlug"
                                                    element={
                                                        <DecisionWithAccessCheck
                                                            hasDecision={
                                                                hasDecision
                                                            }
                                                            isDecisionUser={
                                                                isDecisionUser
                                                            }
                                                            isSupportUser={
                                                                isSupportUser
                                                            }
                                                        >
                                                            <Dashboard />
                                                        </DecisionWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.decisionCta}
                                                    key="decisionCta"
                                                    element={
                                                        <CallToActionDecision />
                                                    }
                                                />
                                                <Route
                                                    path={routes.dashboardPdf}
                                                    key="dashboardSlug"
                                                    element={
                                                        <DecisionWithAccessCheck
                                                            hasDecision={
                                                                hasDecision
                                                            }
                                                            isDecisionUser={
                                                                isDecisionUser
                                                            }
                                                            isSupportUser={
                                                                isSupportUser
                                                            }
                                                        >
                                                            <Dashboard />
                                                        </DecisionWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={
                                                        routes.publicDashboard
                                                    }
                                                    key="publicDashboardSlug"
                                                    element={
                                                        <PublicDashboardNotAccessiblePageWithRedirection
                                                            isDecisionUser={
                                                                isDecisionUser
                                                            }
                                                            hasDecision={
                                                                hasDecision
                                                            }
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path={routes.budgetProposal}
                                                    key="budgetProposalId"
                                                    element={
                                                        <BudgetWithAccessCheck
                                                            hasBudget={
                                                                hasBudget
                                                            }
                                                            isBudgetUser={
                                                                isBudgetUser
                                                            }
                                                        >
                                                            <BudgetWithProvider>
                                                                <BudgetProposal />
                                                            </BudgetWithProvider>
                                                        </BudgetWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.budgetCta}
                                                    key="budgetCta"
                                                    element={
                                                        <CallToActionBudget />
                                                    }
                                                />
                                                <Route
                                                    path={
                                                        routes.budgetProposalByResource
                                                    }
                                                    key="budgetProposalIdTab"
                                                    element={
                                                        <ResourceWithRouting
                                                            isBudgetUser={
                                                                isBudgetUser
                                                            }
                                                            hasBudget={
                                                                hasBudget
                                                            }
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path={
                                                        routes.budgetProposalArgument
                                                    }
                                                    key="budgetProposalIdArgumentsId"
                                                    element={
                                                        <BudgetWithAccessCheck
                                                            hasBudget={
                                                                hasBudget
                                                            }
                                                            isBudgetUser={
                                                                isBudgetUser
                                                            }
                                                        >
                                                            <BudgetWithProvider>
                                                                <Resource />
                                                            </BudgetWithProvider>
                                                        </BudgetWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={
                                                        routes.budgetProposalArgumentPdf
                                                    }
                                                    key="budgetProposalIdArgumentsIdPdf"
                                                    element={
                                                        <BudgetWithAccessCheck
                                                            hasBudget={
                                                                hasBudget
                                                            }
                                                            isBudgetUser={
                                                                isBudgetUser
                                                            }
                                                        >
                                                            <BudgetWithProvider>
                                                                <Resource />
                                                            </BudgetWithProvider>
                                                        </BudgetWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={
                                                        routes.budgetProposalExpenseOrIncome
                                                    }
                                                    key="budgetProposalIdExpenseIncomeTabViewByResource"
                                                    element={
                                                        <BudgetWithAccessCheck
                                                            hasBudget={
                                                                hasBudget
                                                            }
                                                            isBudgetUser={
                                                                isBudgetUser
                                                            }
                                                        >
                                                            <BudgetWithProvider>
                                                                <Resource />
                                                            </BudgetWithProvider>
                                                        </BudgetWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={
                                                        routes.budgetProposalProjection
                                                    }
                                                    key="projections"
                                                    element={
                                                        <BudgetWithAccessCheck
                                                            hasBudget={
                                                                hasBudget
                                                            }
                                                            isBudgetUser={
                                                                isBudgetUser
                                                            }
                                                        >
                                                            <BudgetWithProvider>
                                                                <Resource />
                                                            </BudgetWithProvider>
                                                        </BudgetWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.budgetHome}
                                                    key="budgetHome"
                                                    element={
                                                        <BudgetWithAccessCheck
                                                            hasBudget={
                                                                hasBudget
                                                            }
                                                            isBudgetUser={
                                                                isBudgetUser
                                                            }
                                                        >
                                                            <BudgetWithProvider>
                                                                <BudgetLibrary />
                                                            </BudgetWithProvider>
                                                        </BudgetWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.budgetOverview}
                                                    key="budgetOverviewTabExpenseOrIncome"
                                                    element={
                                                        <BudgetWithAccessCheck
                                                            hasBudget={
                                                                hasBudget
                                                            }
                                                            isBudgetUser={
                                                                isBudgetUser
                                                            }
                                                        >
                                                            <BudgetOverviewWithAccessCheck>
                                                                <BudgetWithProvider>
                                                                    <BudgetOverview />
                                                                </BudgetWithProvider>
                                                            </BudgetOverviewWithAccessCheck>
                                                        </BudgetWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.operationView}
                                                    key="operationView"
                                                    element={
                                                        <BudgetWithAccessCheck
                                                            hasBudget={
                                                                hasBudget
                                                            }
                                                            isBudgetUser={
                                                                isBudgetUser
                                                            }
                                                        >
                                                            <BudgetWithProvider>
                                                                <Operation360 />
                                                            </BudgetWithProvider>
                                                        </BudgetWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.homePage}
                                                    key="homePage"
                                                    element={<Home />}
                                                />
                                                <Route
                                                    path={routes.dashboards}
                                                    key="dashboards"
                                                    element={
                                                        <DecisionWithAccessCheck
                                                            hasDecision={
                                                                hasDecision
                                                            }
                                                            isDecisionUser={
                                                                isDecisionUser
                                                            }
                                                            isSupportUser={
                                                                isSupportUser
                                                            }
                                                        >
                                                            <Dashboards />
                                                        </DecisionWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.indicators}
                                                    key="indicators"
                                                    element={
                                                        <IndicatorsWithAccessCheck>
                                                            <Indicators />
                                                        </IndicatorsWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={
                                                        routes.hrSimulationHome
                                                    }
                                                    key="humanResourcesSimulationHome"
                                                    element={
                                                        <HumanResourcesWithAccessCheck
                                                            isHumanResourcesUser={
                                                                isHumanResourcesUser
                                                            }
                                                            hasHumanResources={
                                                                hasHumanResources
                                                            }
                                                        >
                                                            <HumanResourcesProvider>
                                                                <ObsoleteOrgChartSimulationProvider>
                                                                    <SimulationHome />
                                                                </ObsoleteOrgChartSimulationProvider>
                                                            </HumanResourcesProvider>
                                                        </HumanResourcesWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.hrCta}
                                                    key="humanResourcesCta"
                                                    element={
                                                        <CallToActionHumanResources />
                                                    }
                                                />
                                                <Route
                                                    path={routes.hrSettings}
                                                    key="humanResourcesSettings"
                                                    element={
                                                        <HumanResourcesWithAccessCheck
                                                            isHumanResourcesUser={
                                                                isHumanResourcesUser
                                                            }
                                                            hasHumanResources={
                                                                hasHumanResources
                                                            }
                                                        >
                                                            <HumanResourcesProvider>
                                                                <HrSettings />
                                                            </HumanResourcesProvider>
                                                        </HumanResourcesWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.hrPayStateHome}
                                                    key="humanResourcesPayStateHome"
                                                    element={
                                                        <HumanResourcesWithAccessCheck
                                                            isHumanResourcesUser={
                                                                isHumanResourcesUser
                                                            }
                                                            hasHumanResources={
                                                                hasHumanResources
                                                            }
                                                        >
                                                            <HumanResourcesProvider>
                                                                <PayStateHome />
                                                            </HumanResourcesProvider>
                                                        </HumanResourcesWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.hrPayStateTab}
                                                    key="humanResourcesPayState"
                                                    element={
                                                        <HumanResourcesWithAccessCheck
                                                            isHumanResourcesUser={
                                                                isHumanResourcesUser
                                                            }
                                                            hasHumanResources={
                                                                hasHumanResources
                                                            }
                                                        >
                                                            <HumanResourcesProvider>
                                                                <PayStateSynthesis />
                                                            </HumanResourcesProvider>
                                                        </HumanResourcesWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={
                                                        routes.hrRetrospectiveHome
                                                    }
                                                    key="humanResourcesRetrospectiveHome"
                                                    element={
                                                        <HumanResourcesWithAccessCheck
                                                            isHumanResourcesUser={
                                                                isHumanResourcesUser
                                                            }
                                                            hasHumanResources={
                                                                hasHumanResources
                                                            }
                                                        >
                                                            <HumanResourcesProvider>
                                                                <RetrospectiveHome />
                                                            </HumanResourcesProvider>
                                                        </HumanResourcesWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={
                                                        routes.hrRetrospective
                                                    }
                                                    key="humanResourcesRetrospectiveSynthesis"
                                                    element={
                                                        <HumanResourcesWithAccessCheck
                                                            isHumanResourcesUser={
                                                                isHumanResourcesUser
                                                            }
                                                            hasHumanResources={
                                                                hasHumanResources
                                                            }
                                                        >
                                                            <HumanResourcesProvider>
                                                                <RetrospectiveSynthesis />
                                                            </HumanResourcesProvider>
                                                        </HumanResourcesWithAccessCheck>
                                                    }
                                                />

                                                <Route
                                                    path={routes.hrPayState}
                                                    key="humanResourcesPayStateRedirectDiary"
                                                    element={
                                                        <Navigate
                                                            to="diary"
                                                            replace
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path={
                                                        routes.hrSimulationTab
                                                    }
                                                    key="humanResourcesHrSimulation"
                                                    element={
                                                        <HumanResourcesWithAccessCheck
                                                            isHumanResourcesUser={
                                                                isHumanResourcesUser
                                                            }
                                                            hasHumanResources={
                                                                hasHumanResources
                                                            }
                                                        >
                                                            <HumanResourcesProvider>
                                                                <ObsoleteOrgChartSimulationProvider>
                                                                    <SimulationSynthesis />
                                                                </ObsoleteOrgChartSimulationProvider>
                                                            </HumanResourcesProvider>
                                                        </HumanResourcesWithAccessCheck>
                                                    }
                                                />
                                                <Route
                                                    path={routes.hrSimulation}
                                                    key="humanResourcesSimulationRedirectScenario"
                                                    element={
                                                        <Navigate
                                                            to="scenario"
                                                            replace
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path="*"
                                                    key="notFound"
                                                    element={<NotFound />}
                                                />
                                            </>
                                        )}
                                    {!isAuthenticated && (
                                        <>
                                            {showLogin && (
                                                <Route
                                                    path="*"
                                                    element={<LoginPage />}
                                                />
                                            )}
                                            {!showLogin && hasDecision && (
                                                <Route
                                                    path={
                                                        routes.publicDashboard
                                                    }
                                                    element={
                                                        <Dashboard
                                                            publicToken={
                                                                publicToken
                                                            }
                                                            isPublic
                                                        />
                                                    }
                                                />
                                            )}
                                        </>
                                    )}
                                </Routes>
                            </ErrorBoundary>
                        </AppWithSideBar>
                        {/* otherwise we show useless network error message when the maintenance mode is active */}
                        {!isMaintenance && (
                            <SnackbarNotification
                                open={showSnack}
                                message={snackText}
                                variant={snackType}
                                action={snackAction}
                                close={closeSnack}
                            />
                        )}
                        <MaintenanceDialog />
                    </div>
                </ThemeProvider>
            </StyledEngineProvider>
        </QueryClientProvider>
    );
}
